import type {JSX} from 'react'
import {useState, useEffect, useCallback, useMemo} from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {getCookie} from 'cookies-next'
import {useRouter} from 'next/router'
import {Button as CButton} from '@/_new-code/products/flexible-web-toolkit/components/button/button'
import {RichTextRenderer} from '@/_new-code/products/flexible-web-toolkit/components/rich-text-renderer'
import type {
	AdvancedPageTemplateContentItem,
	GlobalConfigContentItem,
	Tersed,
} from '@/_new-code/services/kontent-ai/types'
import type {GeoLocationApiResponse} from '@/_new-code/services/geo-location-popup/geo-location-api-handler'
import type {Result} from '@/_new-code/utilities/types'
import type {BlockMapper as Mapper} from '@/_new-code/services/kontent-ai/mapper'
import {pushToDataLayer} from '@/utils/analytics'
import {isFeatureEnabled} from '@/_new-code/utilities/feature-enabled'
import {loadImageFromKontentAI} from '../../../imageLoader'
import {ImageBlock} from '../flexible-web-toolkit/blocks/image'

function findHrefsByLocale(hreflangs: string, locale: string): string[] {
	const regex =
		/<link\s+rel="alternate"\s+href="(?<href>[^"]+)"\s+hreflang="(?<hreflang>[^"]+)"\s*\/?>/g
	const matches: string[] = []
	const lowerLocale = locale.toLowerCase().replace('_', '-')
	let match: RegExpExecArray | null

	while ((match = regex.exec(hreflangs)) !== null) {
		const href = match.groups?.href ?? ''
		const hreflang = match.groups?.hreflang?.toLowerCase()
		if (hreflang === lowerLocale) {
			matches.push(href)
		}
	}
	return matches
}

const LoadingImage = (): JSX.Element => (
	<div className="mb-4 flex justify-center">
		<div className="flex animate-pulse items-center gap-3">
			<div className="h-[50px] w-[50px] rounded bg-gray-300" />
		</div>
	</div>
)

const LoadingTitle = (): JSX.Element => (
	<div className="animate-pulse">
		<div className="mx-auto h-8 w-3/4 rounded bg-gray-300" />
	</div>
)

const LoadingContent = (): JSX.Element => (
	<div className="animate-pulse px-5">
		<div className="space-y-3">
			<div className="mx-auto h-4 w-full rounded bg-gray-300" />
			<div className="mx-auto h-4 w-5/6 rounded bg-gray-300" />
			<div className="mx-auto h-4 w-4/5 rounded bg-gray-300" />
		</div>
	</div>
)

const LoadingButton = (): JSX.Element => (
	<div className="animate-pulse">
		<div className="mb-3 h-12 w-48 rounded bg-gray-300 sm:mb-0" />
	</div>
)

const LoadingLinkText = (): JSX.Element => (
	<div className="animate-pulse">
		<div className="mx-auto h-4 w-32 rounded bg-gray-300" />
	</div>
)

export const GeoLocationBlock = ({
	globalConfig,
	page,
	BlockMapper,
}: {
	globalConfig: Tersed<GlobalConfigContentItem>
	page: Tersed<AdvancedPageTemplateContentItem>
	BlockMapper: typeof Mapper
}): JSX.Element => {
	const router = useRouter()
	const [showBanner, setShowBanner] = useState(false)
	const [geoPopup, setGeoPopup] = useState<GeoLocationApiResponse | null>(
		null
	)
	const [isLoadingFlag, setIsLoadingFlag] = useState(false)
	const cookie = getCookie('X-Origin-Country')
	const [originCountry, setOriginCountry] = useState<string>(() =>
		typeof cookie === 'string' ? cookie : ''
	)
	const isGeolocationEnabled = isFeatureEnabled(
		'geoLocationPopup',
		globalConfig
	)

	const getSessionItem = (key: string): boolean => {
		if (typeof window === 'undefined') return false
		return sessionStorage.getItem(key) === 'true'
	}

	const setSessionItem = (key: string, value: boolean): void => {
		if (typeof window !== 'undefined') {
			sessionStorage.setItem(key, value.toString())
		}
	}

	const fetchGeoPopup = useCallback(
		async (originCountryCode: string) => {
			setIsLoadingFlag(true)
			try {
				const response = await fetch('/api/geo-location/info', {
					method: 'POST',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify({
						originCountry: originCountryCode,
						locale: router.locale,
					}),
				})
				if (response.ok) {
					const result =
						(await response.json()) as Result<GeoLocationApiResponse>
					if (result.success) {
						setGeoPopup(result.data)
					}
				}
			} catch (error) {
				// eslint-disable-next-line no-console -- to check the issue
				console.error('GeoLocation fetch error:', error)
			} finally {
				setIsLoadingFlag(false)
			}
		},
		[router.locale]
	)

	useEffect(() => {
		const cookieValue = getCookie('X-Origin-Country')
		if (cookieValue && cookieValue !== originCountry) {
			setOriginCountry(
				typeof cookieValue === 'string' ? cookieValue.toLowerCase() : ''
			)
		}
	}, [])

	const countries = geoPopup?.origin.countries

	const {title, linkText, defaultImage, defaultContent, defaultButton} =
		globalConfig.elements.geolocationPopup[0]?.elements ?? {}

	useEffect(() => {
		let reqLocale = router.locale
		if (reqLocale?.includes('_')) {
			reqLocale = router.locale?.split('_')[1]
		}
		const showGeoLocationBlock =
			isGeolocationEnabled &&
			globalConfig.elements.geolocationPopup[0] &&
			originCountry &&
			reqLocale !== originCountry &&
			router.locale !== 'global' &&
			!getSessionItem('isGeolocationPromptHandled')
		if (showGeoLocationBlock) {
			void fetchGeoPopup(originCountry)
			setShowBanner(showGeoLocationBlock)
			pushToDataLayer({
				event: 'geolocation_popup_shown',
				user_country: originCountry.toLowerCase(),
				page_url: window.location.href,
			})
		}
	}, [
		fetchGeoPopup,
		globalConfig.elements.geolocationPopup,
		originCountry,
		router.locale,
	])

	const handleCtaButtons = async (
		url: string,
		locale: string
	): Promise<void> => {
		let redirectPath = ''
		const hrefs = findHrefsByLocale(
			page.elements.snippetPageSeoPageHreflangs,
			locale
		)
		redirectPath = hrefs[0] || url

		setSessionItem('isGeolocationPromptHandled', true)
		setShowBanner(false)

		const analyticsPromise = new Promise<void>((resolve) => {
			pushToDataLayer({
				event: 'geolocation_popup_confirmed',
				user_country: originCountry.toLowerCase(),
				page_url: window.location.href,
				redirect_url: redirectPath,
			})

			resolve()
		})

		try {
			await analyticsPromise
			window.location.href = redirectPath
		} catch (error) {
			// eslint-disable-next-line no-console -- Assertions are correct
			console.error(
				'Redirect failed, falling back to location.href',
				error
			)
			window.location.href = redirectPath
		}
	}

	const imageDisplay = useMemo(() => {
		if (geoPopup?.origin.flagImage?.[0]) {
			return (
				<div className="mb-4 flex justify-center">
					<div className="flex items-center gap-3">
						<Image
							alt={
								geoPopup.origin.countries[0]?.name ||
								'Country flag'
							}
							className="flex-shrink-0 rounded"
							height={50}
							loader={loadImageFromKontentAI}
							src={geoPopup.origin.flagImage[0].url}
							width={50}
						/>
					</div>
				</div>
			)
		} else if (defaultImage?.[0]) {
			return (
				<div className="mb-4 flex justify-center">
					<div className="flex items-center gap-3">
						{defaultImage[0] ? (
							<ImageBlock
								BlockMapper={BlockMapper}
								block={defaultImage[0]}
								globalConfig={globalConfig}
								key={defaultImage[0].system.id}
								page={page}
							/>
						) : null}
					</div>
				</div>
			)
		}
		return null
	}, [geoPopup, BlockMapper, globalConfig, page, defaultImage])

	if (isLoadingFlag && showBanner) {
		return (
			<div
				className="fixed inset-y-0 z-[9999] w-full backdrop-blur-[5px] backdrop-filter"
				data-testid="geo-popup"
			>
				<div className="m-auto h-screen bg-[rgba(28,28,28,0.76)] px-4 pb-4 pt-20 xxs:overflow-x-scroll xxs:overflow-y-scroll">
					<div
						className="mx-auto my-0 min-h-[331px] max-w-2xl xxs:min-h-[400px] xxs:w-[100%] xs:min-h-[400px] xs:w-[100%]"
						style={{
							border: 'thick solid #F8F8F8',
							backgroundColor: '#F8F8F8',
							borderRadius: '10px',
							minWidth: '300px',
						}}
					>
						<div className="bg-[#F8F8F8] pb-5 pt-[30px]">
							<div className="pl-5 pr-5 text-center text-[#373737]">
								<div className="text-2xl font-extrabold leading-[1.18]">
									<LoadingTitle />
								</div>
							</div>
						</div>

						<div className="overflow-y-auto overflow-x-hidden border-t-[1px] border-[lightgrey] bg-[#F8F8F8] pb-[15px] pt-[15px]">
							<LoadingImage />
							<LoadingContent />
						</div>

						<div className="mx-auto my-4 flex w-full flex-col items-center justify-center bg-[#F8F8F8] px-4">
							<div className="mb-3 flex w-full flex-col items-center justify-center sm:flex-row sm:gap-3">
								<LoadingButton />
							</div>
							<LoadingLinkText />
						</div>
					</div>
				</div>
			</div>
		)
	}

	if (!showBanner) {
		// eslint-disable-next-line react/jsx-no-useless-fragment -- no need to show anything
		return <></>
	}

	return (
		<div
			className="fixed inset-y-0 z-[9999] w-full backdrop-blur-[5px] backdrop-filter"
			data-testid="geo-popup"
		>
			<div className="m-auto h-screen bg-[rgba(28,28,28,0.76)] px-4 pb-4 pt-20 xxs:overflow-y-scroll">
				<div
					className="mx-auto my-0 min-h-[331px] max-w-2xl xxs:min-h-[400px] xxs:w-[100%] xs:min-h-[400px] xs:w-[100%]"
					style={{
						border: 'thick solid #F8F8F8',
						backgroundColor: '#F8F8F8',
						borderRadius: '10px',
						minWidth: '300px',
					}}
				>
					<div className="bg-[#F8F8F8] pb-5 pt-[30px]">
						<div className="pl-5 pr-5 text-center text-[#373737]">
							<div className="text-2xl font-extrabold leading-[1.18]">
								{/* Title of the pop up */}
								{countries?.length
									? countries.map((country) => {
											return (
												<p
													key={
														country
															.globalConfig?.[0]
															?.system.id
													}
												>
													{
														country
															.globalConfig?.[0]
															?.elements.title
													}
												</p>
											)
										})
									: title}
							</div>
						</div>
					</div>

					<div className=" overflow-y-auto overflow-x-hidden border-t-[1px] border-[lightgrey] bg-[#F8F8F8] pb-[15px] pt-[15px]">
						{imageDisplay}
						{/* RichTextContent */}
						{countries?.length
							? countries.map((country) => {
									return country.globalConfig?.[0]?.elements
										.content ? (
										<RichTextRenderer
											BlockMapper={BlockMapper}
											className="justify-left pl-5 pr-5 text-center text-[#373737]"
											data-kontent-element-codename="content"
											data-testid={`geo-popup-content-${country.locale}`}
											element={
												country.globalConfig[0].elements
													.content
											}
											globalConfig={globalConfig}
											key={
												country.globalConfig[0]?.system
													.id
											}
											page={page}
										/>
									) : null
								})
							: defaultContent && (
									<RichTextRenderer
										BlockMapper={BlockMapper}
										className="justify-left pl-5 pr-5 text-center text-[#373737]"
										data-kontent-element-codename="default_content"
										data-testid="geo-popup-content-default"
										element={defaultContent}
										globalConfig={globalConfig}
										page={page}
									/>
								)}
					</div>

					<div className="mx-auto my-4 flex w-full flex-col items-center justify-center bg-[#F8F8F8] px-4">
						<div className="mb-3 flex w-full flex-col items-center justify-center sm:flex-row sm:gap-3">
							{/* CTA Button */}
							{countries?.length ? (
								countries.map((country) => {
									return (
										<CButton
											className="ml-2 mr-2 w-auto max-w-[calc(100%-2rem)] xxs:mb-2 xxs:ml-0 xxs:w-full xxs:last:mb-0 xs:mb-2 xs:w-full xs:last:mb-0"
											data-testid={`geo-popup-cta-${country.locale}`}
											key={country.name}
											onClick={async () => {
												await handleCtaButtons(
													country.homePage,
													country.locale
												)
											}}
										>
											{
												country.globalConfig?.[0]
													?.elements.button[0]
													?.elements.text
											}
										</CButton>
									)
								})
							) : (
								<Link
									className="btn mb-3 inline-flex w-auto max-w-[calc(100%-2rem)] cursor-pointer  items-center justify-center overflow-hidden rounded-sm border-2 border-primary bg-theme-main px-4  py-3 text-center align-middle font-bold text-white hover:bg-theme-highlight focus:bg-secondary sm:mb-0"
									data-testid="geo-popup-cta-default"
									href="/country-selector?redirected=true"
									locale={false}
									onClick={() => {
										setShowBanner(false)
										setSessionItem(
											'isGeolocationPromptHandled',
											true
										)
										pushToDataLayer({
											event: 'geolocation_popup_confirmed',
											user_country:
												originCountry.toLowerCase(),
											page_url: window.location.href,
											redirect_url:
												'/country-selector?redirected=true',
										})
									}}
								>
									{defaultButton?.[0]?.elements.text}
								</Link>
							)}
						</div>
						<Link
							className="text-center text-sm hover:underline"
							data-testid="geo-popup-continue in current country"
							href={router.asPath}
							onClick={() => {
								setShowBanner(false)
								setSessionItem(
									'isGeolocationPromptHandled',
									true
								)
								pushToDataLayer({
									event: 'geolocation_popup_confirmed',
									user_country: originCountry.toLowerCase(),
									page_url: window.location.href,
									redirect_url: window.location.href,
								})
							}}
						>
							{linkText}
						</Link>
					</div>
				</div>
			</div>
		</div>
	)
}
